import axios from "axios";
import { io } from "socket.io-client";

const API_BASE_URL =
  import.meta.env.VITE_API_URL || "https://dashboard.lono.co.in/api";

// Create axios instance with auth token
const createAuthInstance = () => {
  const token =
    localStorage.getItem("adminToken") ||
    localStorage.getItem("staffToken") ||
    localStorage.getItem("customerToken") ||
    localStorage.getItem("bankingPartnerToken") ||
    localStorage.getItem("affiliatePartnerToken") ||
    localStorage.getItem("creditManagerToken") ||
    localStorage.getItem("token");

  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

// Communication API Service
export const communicationAPI = {
  // Conversation Management
  createConversation: async (
    participants,
    conversationType = "direct",
    title,
    description
  ) => {
    try {
      const response = await createAuthInstance().post(
        "/communication/conversations",
        {
          participants,
          conversationType,
          title,
          description,
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getConversations: async (page = 1, limit = 20, search = "") => {
    try {
      const params = new URLSearchParams({ page, limit });
      if (search) params.append("search", search);

      const response = await createAuthInstance().get(
        `/communication/conversations?${params}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Message Management
  getMessages: async (conversationId, page = 1, limit = 50) => {
    try {
      const params = new URLSearchParams({ page, limit });
      const response = await createAuthInstance().get(
        `/communication/conversations/${conversationId}/messages?${params}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getUnreadCounts: async () => {
    try {
      const response = await createAuthInstance().get(
        "/communication/unread-counts"
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  sendMessage: async (
    conversationId,
    content,
    messageType = "text",
    replyTo = null
  ) => {
    try {
      const response = await createAuthInstance().post(
        "/communication/messages",
        {
          conversationId,
          content,
          messageType,
          replyTo,
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  markAsRead: async (messageId) => {
    try {
      const response = await createAuthInstance().put(
        `/communication/messages/${messageId}/read`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  markConversationAsRead: async (conversationId) => {
    try {
      const response = await createAuthInstance().put(
        `/communication/conversations/${conversationId}/read`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  editMessage: async (messageId, content) => {
    try {
      const response = await createAuthInstance().put(
        `/communication/messages/${messageId}`,
        {
          content,
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  deleteMessage: async (messageId) => {
    try {
      const response = await createAuthInstance().delete(
        `/communication/messages/${messageId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // User Status
  getOnlineUsers: async () => {
    try {
      const response = await createAuthInstance().get(
        "/communication/online-users"
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateUserStatus: async (status) => {
    try {
      const response = await createAuthInstance().put(
        "/communication/user-status",
        { status }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Legacy chat endpoints (for backward compatibility)
  getChatHistory: async (user1Id, user1Type, user2Id, user2Type) => {
    try {
      const params = new URLSearchParams({
        user1Id,
        user1Type,
        user2Id,
        user2Type,
      });
      const response = await createAuthInstance().get(
        `/chat/history?${params}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  sendLegacyMessage: async (
    senderId,
    senderType,
    receiverId,
    receiverType,
    message
  ) => {
    try {
      const response = await createAuthInstance().post("/chat/send", {
        senderId,
        senderType,
        receiverId,
        receiverType,
        message,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  sendBulkNotification: async (payload) => {
    try {
      const response = await createAuthInstance().post(
        "/communication/bulk-notification",
        payload
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Socket.IO Service
export class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.eventListeners = new Map();
  }

  connect(token) {
    if (this.socket && this.isConnected) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const SOCKET_URL =
          import.meta.env.VITE_SOCKET_URL || "https://dashboard.lono.co.in";

        // console.log(
        //   "Connecting to socket with token:",
        //   token ? "Token provided" : "No token"
        // );

        this.socket = io(SOCKET_URL, {
          auth: { token },
          query: { token },
          transports: ["websocket", "polling"],
          timeout: 20000,
          forceNew: true,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          maxReconnectionAttempts: 10,
          randomizationFactor: 0.5,
        });

        this.socket.on("connect", () => {
          // console.log("Socket connected:", this.socket.id);
          this.isConnected = true;
          // Auto-join conversations on connect/reconnect
          setTimeout(() => {
            this.joinConversations();
          }, 1000);
          resolve();
        });

        this.socket.on("disconnect", (reason) => {
          // console.log("Socket disconnected:", reason);
          this.isConnected = false;
          // Attempt to reconnect if disconnected unexpectedly
          if (reason === "io server disconnect") {
            this.socket.connect();
          }
        });

        this.socket.on("reconnect", (attemptNumber) => {
          // console.log("Socket reconnected after", attemptNumber, "attempts");
          this.isConnected = true;
          // Re-join conversations after reconnection
          this.joinConversations();
        });

        this.socket.on("reconnect_attempt", (attemptNumber) => {
          // console.log("Socket reconnection attempt:", attemptNumber);
        });

        this.socket.on("reconnect_error", (error) => {
          // console.error("Socket reconnection error:", error);
        });

        this.socket.on("connect_error", (error) => {
          // console.error("Socket connection error:", error);
          this.isConnected = false;
          // Only reject on first connection attempt
          if (!this.socket.recovered) {
            reject(error);
          }
        });

        this.socket.on("error", (error) => {
          // console.error("Socket error:", error);
          this.emitEvent("error", error);
        });

        // Set up default event listeners
        this.setupDefaultListeners();
      } catch (error) {
        // console.error("Error initializing socket:", error);
        reject(error);
      }
    });
  }

  setupDefaultListeners() {
    // User status events
    this.socket.on("userOnline", (data) => {
      this.emitEvent("userOnline", data);
    });

    this.socket.on("userOffline", (data) => {
      this.emitEvent("userOffline", data);
    });

    this.socket.on("userStatusUpdate", (data) => {
      this.emitEvent("userStatusUpdate", data);
    });

    // Message events
    this.socket.on("newMessage", (data) => {
      this.emitEvent("newMessage", data);
    });

    this.socket.on("messageDelivered", (data) => {
      this.emitEvent("messageDelivered", data);
    });

    this.socket.on("messageRead", (data) => {
      this.emitEvent("messageRead", data);
    });

    this.socket.on("messageEdited", (data) => {
      this.emitEvent("messageEdited", data);
    });

    this.socket.on("messageDeleted", (data) => {
      this.emitEvent("messageDeleted", data);
    });

    // Typing indicators
    this.socket.on("userTyping", (data) => {
      this.emitEvent("userTyping", data);
    });

    this.socket.on("userStoppedTyping", (data) => {
      this.emitEvent("userStoppedTyping", data);
    });

    // Conversation events
    this.socket.on("conversationsJoined", (data) => {
      this.emitEvent("conversationsJoined", data);
    });

    this.socket.on("conversationJoined", (data) => {
      this.emitEvent("conversationJoined", data);
    });

    // New conversation created
    this.socket.on("newConversation", (data) => {
      this.emitEvent("newConversation", data);
    });

    // Unread counts update
    this.socket.on("unreadCountsUpdate", (data) => {
      this.emitEvent("unreadCountsUpdate", data);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Join conversations
  joinConversations() {
    if (this.socket && this.isConnected) {
      // console.log('🔗 Emitting joinConversations event');
      this.socket.emit("joinConversations");
    } else {
      // console.log('⚠️ Cannot join conversations - socket not connected:', {
      //   hasSocket: !!this.socket,
      //   isConnected: this.isConnected
      // });
    }
  }

  joinConversation(conversationId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("joinConversation", { conversationId });
    }
  }

  // Mark message as read
  markAsRead(messageId) {
    if (this.socket && this.isConnected) {
      this.socket.emit("markAsRead", { messageId });
    }
  }

  // Typing indicators
  setTyping(conversationId, isTyping) {
    if (this.socket && this.isConnected) {
      this.socket.emit("typing", { conversationId, isTyping });
    }
  }

  // Update status
  updateStatus(status) {
    if (this.socket && this.isConnected) {
      this.socket.emit("updateStatus", { status });
    }
  }

  // Event listeners
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emitEvent(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Emit method to send events to server
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`Cannot emit ${event} - socket not connected`);
    }
  }

  // Utility methods
  isConnected() {
    return this.isConnected;
  }

  getSocketId() {
    return this.socket ? this.socket.id : null;
  }
}

// Create singleton instance
export const socketService = new SocketService();

// Export default
export default {
  communicationAPI,
  socketService,
  SocketService,
};
